import React, { useMemo } from 'react';

import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useGetTypicalMonthsQuery } from 'state/apiSlice';
import { SavedRouteAlertMetricType } from 'state/emailAlertsSlices';

import dayjs, { Dayjs } from 'dayjs';
import { parseMonthYear } from 'features/common/datetimeUtils';
import { InfoWalkmeCrumb } from 'features/info_hover/InfoHover';
import { secondary_light_text_color } from 'theme/cemTheme';

import { MetricType } from '../alertHelpers';
import { MetricRow } from '../components/MetricRow';
import { useNotificationProject } from '../useNotificationProject';

import styles from './AlertMetricField.module.css';

const thresholdStep = 5;
const thresholdMin = 5;
const thresholdMax = 100;

interface AlertMetricFieldProps {
  mode: 'view' | 'edit';
  // % slowdown and comparison month are read-only when updating an existing alert.
  metricThresholdLocked?: boolean;
  metricType: SavedRouteAlertMetricType;
  metricCriteria: number;
  typicalYearNum?: number | null;
  typicalMonthNum?: number | null;
  onMetricTypeChange?: (value: SavedRouteAlertMetricType) => void;
  onMetricCriteriaChange?: (value: number) => void;
  onTypicalYearChange?: (year: number | null) => void;
  onTypicalMonthChange?: (month: number | null) => void;
}

export function AlertMetricField({
  mode,
  metricThresholdLocked = false,
  metricType,
  metricCriteria,
  typicalYearNum,
  typicalMonthNum,
  onMetricCriteriaChange,
  onTypicalYearChange,
  onTypicalMonthChange,
}: AlertMetricFieldProps) {
  const isEdit = mode === 'edit';
  const isMetricThresholdEditable = isEdit && !metricThresholdLocked;
  const isPercentSlowdown = metricType === MetricType.PERCENT_SLOWDOWN;
  const metricCriteriaPercent = Math.round(metricCriteria * 100);
  const { projectSlug: project_slug } = useNotificationProject();

  const { data: typicalMonthsInfo } = useGetTypicalMonthsQuery(project_slug, {
    skip: !project_slug,
  });

  const { availableMonths, availableYears } = useMemo(() => {
    if (!typicalMonthsInfo) {
      return {
        availableMonths: [] as string[],
        availableYears: new Set<number>(),
      };
    }

    const monthKeys: string[] = [];
    const yearSet = new Set<number>();

    typicalMonthsInfo.forEach((monthInfo) => {
      const parsed = parseMonthYear(monthInfo.typicalMonth);
      const monthKey = parsed.format('YYYY-MM');
      monthKeys.push(monthKey);
      yearSet.add(parsed.year());
    });

    const availableMonths = [...new Set(monthKeys)];
    availableMonths.sort();
    return { availableMonths, availableYears: yearSet };
  }, [typicalMonthsInfo]);

  const minMonthYear = availableMonths[0];
  const maxMonthYear = availableMonths[availableMonths.length - 1];

  const shouldDisableMonth = (month: Dayjs) => {
    const monthKey = month.format('YYYY-MM');
    return !availableMonths.includes(monthKey);
  };

  const shouldDisableYear = (year: Dayjs) => {
    return !availableYears.has(year.year());
  };

  const handleSliderChange = (
    event: Event,
    newValue: number | number[],
  ): void => {
    if (typeof newValue === 'number' && onMetricCriteriaChange) {
      const roundedValue = Math.round(newValue) / 100;
      onMetricCriteriaChange(roundedValue);
    }
  };

  const comparisonDate =
    typicalYearNum && typicalMonthNum
      ? dayjs()
          .year(typicalYearNum)
          .month(typicalMonthNum - 1)
          .date(1)
      : null;

  const handleDateChange = (newValue: Dayjs | null) => {
    if (onTypicalYearChange && onTypicalMonthChange) {
      if (newValue) {
        onTypicalYearChange(newValue.year());
        onTypicalMonthChange(newValue.month() + 1);
      } else {
        onTypicalYearChange(null);
        onTypicalMonthChange(null);
      }
    }
  };

  return (
    <Stack spacing={3}>
      <Stack spacing={2}>
        <Typography variant="h4" color={secondary_light_text_color}>
          Set Metric Threshold
        </Typography>
        {/* Currently we only have percent slowdown as a metric. */}
        {isPercentSlowdown ? (
          <MetricRow label="% Travel Time Increase">
            <Box className={styles.sliderContainer}>
              <Slider
                value={
                  typeof metricCriteria === 'number' ? metricCriteriaPercent : 0
                }
                onChange={handleSliderChange}
                min={thresholdMin}
                max={thresholdMax}
                step={thresholdStep}
                color="primary"
                disabled={!isMetricThresholdEditable}
                className={
                  !isMetricThresholdEditable ? styles.sliderDisabled : undefined
                }
                sx={{ flex: 1 }}
              />
              <Box className={`${styles.chipTitle} ${styles.chipContainer}`}>
                <Typography variant="body2">
                  +{metricCriteriaPercent}%
                </Typography>
              </Box>
              <InfoWalkmeCrumb elId="slowdown-metric-threshold" />
            </Box>
          </MetricRow>
        ) : (
          <Typography variant="body2">
            Other metric not available yet.
          </Typography>
        )}

        <MetricRow label="Comparison Month">
          {isMetricThresholdEditable ? (
            <Box sx={{ width: '16rem' }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={comparisonDate}
                  onChange={handleDateChange}
                  views={['year', 'month']}
                  format="MMMM YYYY"
                  minDate={minMonthYear ? dayjs(minMonthYear) : undefined}
                  maxDate={maxMonthYear ? dayjs(maxMonthYear) : undefined}
                  shouldDisableMonth={shouldDisableMonth}
                  shouldDisableYear={shouldDisableYear}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          ) : (
            <Typography
              variant="body1"
              className={styles.comparisonMonthContainer}
            >
              {comparisonDate ? comparisonDate.format('MMMM YYYY') : 'Not set'}
            </Typography>
          )}
        </MetricRow>
      </Stack>
    </Stack>
  );
}
